// src/components/staff/StaffTab.jsx
import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Alert, CircularProgress, Paper,
  Tabs, Tab, TablePagination
} from "@mui/material";
import { Edit, Delete, Add } from "@mui/icons-material";
import AddStaffDialog from "./AddStaffDialog";
import EditStaffDialog from "./EditStaffDialog";
import RoleDialog from "./RoleDialog";

const getApiBaseUrl = () => process.env.REACT_APP_API_URL || "http://localhost:8000";
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Token ${token}` })
  };
};

export default function StaffTab() {
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [staffPage, setStaffPage] = useState(0);
  const [rolesPage, setRolesPage] = useState(0);
  const PAGE_SIZE = 5;

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);

  // Role dialog state
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);

  useEffect(() => {
    fetchAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchStaff(), fetchRoles()]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const resp = await fetch(`${getApiBaseUrl()}/api/staff/`, { headers: getAuthHeaders() });
      if (!resp.ok) throw new Error("Failed to fetch staff");
      const data = await resp.json();
      setStaff(data);
      setError("");
    } catch (err) {
      console.error(err);
      setError("Network error fetching staff");
    }
  };

  const fetchRoles = async () => {
    try {
      const resp = await fetch(`${getApiBaseUrl()}/api/staff/roles/`, { headers: getAuthHeaders() });
      if (!resp.ok) return setRoles([]);
      const data = await resp.json();
      setRoles(data);
    } catch (err) {
      console.error(err);
      setRoles([]);
    }
  };

  const handleAddClick = () => {
    setAddDialogOpen(true);
  };

  const handleEditClick = (staffMember) => {
    setEditingStaff(staffMember);
    setEditDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this staff member?")) return;
    try {
      const resp = await fetch(`${getApiBaseUrl()}/api/staff/${id}/`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });
      if (!resp.ok) throw new Error("Failed to delete staff");
      setSuccess("Staff deleted successfully");
      setTimeout(() => setSuccess(""), 3000);
      fetchStaff();
    } catch (err) {
      console.error(err);
      setError("Failed to delete staff");
    }
  };

  const handleSaved = () => {
    setSuccess("Saved successfully");
    setTimeout(() => setSuccess(""), 3000);
    fetchAll();
  };

  // Role CRUD functions
  const handleAddRoleClick = () => {
    setRoleDialogOpen(true);
  };

  const handleEditRole = async (role) => {
    const newName = prompt("Enter new role name:", role.name);
    if (newName && newName.trim() && newName !== role.name) {
      try {
        const resp = await fetch(`${getApiBaseUrl()}/api/staff/roles/${role.id}/`, {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({ name: newName.trim() })
        });
        if (!resp.ok) throw new Error("Failed to update role");
        setSuccess("Role updated successfully");
        setTimeout(() => setSuccess(""), 3000);
        fetchRoles();
      } catch (err) {
        console.error(err);
        setError("Failed to update role");
      }
    }
  };

  const handleDeleteRole = async (roleId, roleName) => {
    if (!window.confirm(`Delete role "${roleName}"? This might affect staff members with this role.`)) return;
    try {
      const resp = await fetch(`${getApiBaseUrl()}/api/staff/roles/${roleId}/`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });
      if (!resp.ok) throw new Error("Failed to delete role");
      setSuccess("Role deleted successfully");
      setTimeout(() => setSuccess(""), 3000);
      fetchRoles();
    } catch (err) {
      console.error(err);
      setError("Failed to delete role");
    }
  };

  const handleRoleAdded = () => {
    setSuccess("Role added successfully");
    setTimeout(() => setSuccess(""), 3000);
    fetchRoles();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Staff" />
          <Tab label="Roles" />
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">Staff</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={handleAddClick}>
              Add Staff
            </Button>
          </Box>

          {staff.length === 0 ? (
            <Typography>No staff members found.</Typography>
          ) : (
            <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Entry</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {staff.slice(staffPage * PAGE_SIZE, (staffPage + 1) * PAGE_SIZE).map(s => (
                    <TableRow key={s.id} hover>
                      <TableCell>
                        {s.user_details
                          ? `${s.user_details.first_name || ""} ${s.user_details.last_name || ""}`.trim() || s.user_details.username
                          : "Unknown"}
                      </TableCell>
                      <TableCell>{s.role_name || "No role"}</TableCell>
                      <TableCell>{new Date(s.entry_date).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleEditClick(s)} color="primary"><Edit /></IconButton>
                        <IconButton size="small" onClick={() => handleDelete(s.id)} color="error"><Delete /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={staff.length}
              page={staffPage}
              onPageChange={(e, newPage) => setStaffPage(newPage)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[5]}
            />
            </>
          )}
        </>
      )}

      {activeTab === 1 && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">Roles</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={handleAddRoleClick}>
              Add Role
            </Button>
          </Box>

          {roles.length === 0 ? (
            <Typography>No roles found.</Typography>
          ) : (
            <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Role Name</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roles.slice(rolesPage * PAGE_SIZE, (rolesPage + 1) * PAGE_SIZE).map(role => (
                    <TableRow key={role.id} hover>
                      <TableCell>{role.name}</TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          onClick={() => handleEditRole(role)} 
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteRole(role.id, role.name)} 
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={roles.length}
              page={rolesPage}
              onPageChange={(e, newPage) => setRolesPage(newPage)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[5]}
            />
            </>
          )}
        </Box>
      )}

      {/* Staff Dialogs */}
      <AddStaffDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSaved={handleSaved}
        roles={roles}
      />

      {editingStaff && (
        <EditStaffDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          staffMember={editingStaff}
          onSaved={handleSaved}
          roles={roles}
        />
      )}

      {/* Role Dialog */}
      <RoleDialog
        open={roleDialogOpen}
        onClose={() => setRoleDialogOpen(false)}
        onRoleAdded={handleRoleAdded}
      />
    </Box>
  );
}